/**
 * Dialog hook for HTML5 <dialog> element integration with Phoenix LiveView
 *
 * Handles opening/closing the dialog based on LiveView state changes,
 * and syncs close events back to the server.
 */
export default {
  mounted() {
    // Set up event listeners
    this.setupEventListeners();

    // Open dialog if initial state is true
    const shouldShow = this.el.dataset.show === "true";
    if (shouldShow && !this.el.open) {
      this.openDialog();
    }
  },

  updated() {
    // React to LiveView state changes
    const shouldShow = this.el.dataset.show === "true";
    const isOpen = this.el.open;

    if (shouldShow && !isOpen) {
      this.openDialog();
    } else if (!shouldShow && isOpen) {
      this.closeDialog();
    }
  },

  setupEventListeners() {
    // Listen for native close event
    this.closeHandler = (e) => {
      this.handleClose(e);
    };
    this.el.addEventListener("close", this.closeHandler);

    // Listen for native cancel event (Escape key)
    this.cancelHandler = (e) => {
      this.handleCancel(e);
    };
    this.el.addEventListener("cancel", this.cancelHandler);

    // Listen for backdrop clicks
    this.clickHandler = (e) => {
      this.handleBackdropClick(e);
    };
    this.el.addEventListener("click", this.clickHandler);
  },

  openDialog() {
    try {
      this.el.showModal(); // Opens as modal (with backdrop, focus trap)
    } catch (err) {
      console.error("[Dialog] Failed to open dialog:", this.el.id, err);
    }
  },

  closeDialog() {
    if (this.el.open) {
      // Mark that we're closing programmatically (initiated by server state change)
      this.closingProgrammatically = true;
      this.el.close();
      // Reset flag after a tick
      setTimeout(() => {
        this.closingProgrammatically = false;
      }, 0);
    }
  },

  handleClose(e) {
    // Dialog was closed (programmatically or by form method="dialog")
    // Only notify server if this wasn't a programmatic close initiated by server state
    if (!this.closingProgrammatically) {
      this.notifyServer();
    }
  },

  handleCancel(e) {
    // User pressed Escape key
    // Allow default behavior (closes dialog), then notify server
    // Mark as programmatic so handleClose doesn't double-notify
    this.closingProgrammatically = true;
    setTimeout(() => {
      this.notifyServer();
      this.closingProgrammatically = false;
    }, 0);
  },

  handleBackdropClick(e) {
    // Check if click was on the dialog backdrop (not content)
    const rect = this.el.getBoundingClientRect();
    const isInDialog = (
      rect.top <= e.clientY &&
      e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX &&
      e.clientX <= rect.left + rect.width
    );

    if (!isInDialog) {
      // Click was on backdrop - close directly and let handleClose notify server
      // Don't mark as programmatic - we want handleClose to notify the server
      this.el.close();
    }
  },

  notifyServer() {
    // Push close event to LiveView if configured
    const closeEvent = this.el.dataset.onClose;
    if (closeEvent) {
      this.pushEvent(closeEvent, {});
    }
  },

  destroyed() {
    // Clean up event listeners
    if (this.closeHandler) {
      this.el.removeEventListener("close", this.closeHandler);
    }
    if (this.cancelHandler) {
      this.el.removeEventListener("cancel", this.cancelHandler);
    }
    if (this.clickHandler) {
      this.el.removeEventListener("click", this.clickHandler);
    }
  }
};
